import apiClient from './client';
import { ApiResponse } from '@/types';

/**
 * Feedback types
 */
export type FeedbackType = 'positive' | 'negative' | 'neutral';
export type FeedbackRating = 'accurate' | 'partially_accurate' | 'inaccurate' | 'not_rated';
export type SourceType = 'code_review' | 'standup' | 'report' | 'alert' | 'other';

/**
 * Feedback item
 */
export interface Feedback {
  _id: string;
  type: FeedbackType;
  rating: FeedbackRating;
  comment?: string;
  sourceId: string;
  sourceType: SourceType;
  aiProvider: string;
  aiModel: string;
  aiResponse?: string;
  userId: {
    _id: string;
    username: string;
    displayName: string;
  };
  issuesFeedback?: Array<{
    issueId: string;
    wasHelpful: boolean;
    wasAccurate: boolean;
    comment?: string;
  }>;
  createdAt: string;
}

/**
 * Feedback statistics
 */
export interface FeedbackStats {
  total: number;
  byType: {
    positive: number;
    negative: number;
    neutral: number;
  };
  byRating: {
    accurate: number;
    partially_accurate: number;
    inaccurate: number;
    not_rated: number;
  };
  accuracyRate: number;
  satisfactionRate: number;
}

/**
 * Provider accuracy
 */
export interface ProviderAccuracy {
  provider: string;
  model: string;
  totalFeedback: number;
  accurateCount: number;
  partiallyAccurateCount: number;
  inaccurateCount: number;
  accuracyRate: number;
  positiveRate: number;
}

/**
 * Feedback trends
 */
export interface FeedbackTrend {
  date: string;
  total: number;
  positive: number;
  negative: number;
  neutral: number;
  accuracyRate: number;
}

/**
 * Issue accuracy
 */
export interface IssueAccuracy {
  totalIssues: number;
  helpfulCount: number;
  accurateCount: number;
  helpfulRate: number;
  accuracyRate: number;
  byCategory: Array<{
    category: string;
    total: number;
    helpful: number;
    accurate: number;
  }>;
}

/**
 * Submit feedback
 */
export async function submitFeedback(data: {
  type: FeedbackType;
  rating: FeedbackRating;
  comment?: string;
  sourceId: string;
  sourceType: SourceType;
  aiProvider: string;
  aiModel: string;
  aiResponse?: string;
  issuesFeedback?: Array<{
    issueId: string;
    wasHelpful: boolean;
    wasAccurate: boolean;
    comment?: string;
  }>;
  metadata?: Record<string, unknown>;
}): Promise<Feedback> {
  const response = await apiClient.post<ApiResponse<Feedback>>('/feedback', data);
  return response.data.data as Feedback;
}

/**
 * Get feedback with filters
 */
export async function getFeedback(params?: {
  type?: FeedbackType;
  rating?: FeedbackRating;
  aiProvider?: string;
  userId?: string;
  days?: number;
  limit?: number;
  offset?: number;
}): Promise<{ feedback: Feedback[]; total: number }> {
  const response = await apiClient.get<
    ApiResponse<{ feedback: Feedback[]; pagination: { total: number } }>
  >('/feedback', { params });
  return {
    feedback: response.data.data?.feedback || [],
    total: response.data.data?.pagination?.total || 0,
  };
}

/**
 * Get feedback stats
 */
export async function getFeedbackStats(days = 30): Promise<FeedbackStats> {
  const response = await apiClient.get<ApiResponse<FeedbackStats>>('/feedback/stats', {
    params: { days },
  });
  return response.data.data as FeedbackStats;
}

/**
 * Get provider accuracies
 */
export async function getProviderAccuracies(): Promise<ProviderAccuracy[]> {
  const response = await apiClient.get<ApiResponse<ProviderAccuracy[]>>('/feedback/providers');
  return response.data.data as ProviderAccuracy[];
}

/**
 * Get feedback trends
 */
export async function getFeedbackTrends(days = 30): Promise<FeedbackTrend[]> {
  const response = await apiClient.get<ApiResponse<FeedbackTrend[]>>('/feedback/trends', {
    params: { days },
  });
  return response.data.data as FeedbackTrend[];
}

/**
 * Get issue accuracy
 */
export async function getIssueAccuracy(): Promise<IssueAccuracy> {
  const response = await apiClient.get<ApiResponse<IssueAccuracy>>('/feedback/issues');
  return response.data.data as IssueAccuracy;
}

/**
 * Get feedback for a specific source
 */
export async function getFeedbackForSource(
  sourceId: string,
  sourceType: SourceType
): Promise<Feedback[]> {
  const response = await apiClient.get<ApiResponse<Feedback[]>>(`/feedback/source/${sourceId}`, {
    params: { sourceType },
  });
  return response.data.data as Feedback[];
}
